import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { calculateDeliveryEstimate } from '@theme/delivery-estimate';
import {
  SELECTED_STATE_STORAGE_KEY,
  markHasSelectedState,
  refreshCartItemSections,
  syncSelectedStateToCart,
} from '@theme/beaumont-cart-state';

/**
 * States without their own DC read stock from their servicing DC's state.
 * Matches the fulfillment-location-lock mapping (ACT→NSW, TAS→VIC, NT→SA).
 * @type {Record<string, string>}
 */
const STOCK_STATE_FALLBACK = { ACT: 'NSW', TAS: 'VIC', NT: 'SA' };

const STORAGE_KEY = SELECTED_STATE_STORAGE_KEY;
const STATE_CHANGE_EVENT = 'state-selector:change';
const LABEL_SELECTOR = '[data-state-selector-label]';

/**
 * @param {string} code
 * @param {string} name
 */
async function publishStateChange(code, name) {
  sessionStorage.setItem(STORAGE_KEY, code);
  markHasSelectedState();

  // Persist to the cart attribute (source of truth on reload). Without this, a selection made
  // here is overwritten by the stale cart attribute when hydrateSelectedStateFromCart runs.
  try {
    await syncSelectedStateToCart(code);
    await refreshCartItemSections();
  } catch (error) {
    console.error('Failed to sync selected state to cart attribute', error);
  }

  document.querySelectorAll(LABEL_SELECTOR).forEach((element) => {
    element.textContent = name;
  });

  const stateSelector = document.querySelector('state-selector-component');
  const radio = stateSelector?.querySelector(
    `input[type="radio"][name="au-state"][value="${CSS.escape(code)}"]`
  );

  if (radio instanceof HTMLInputElement) {
    radio.checked = true;
  }

  document.dispatchEvent(
    new CustomEvent(STATE_CHANGE_EVENT, {
      bubbles: true,
      detail: { code, name },
    })
  );
}

/**
 * @param {string} attribute
 * @param {HTMLElement} element
 * @returns {unknown}
 */
function parseJsonAttribute(attribute, element) {
  const raw = element.getAttribute(attribute);

  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * @param {HTMLElement} element
 * @returns {{ etaWindows: import('@theme/delivery-estimate').EtaWindowsMap, bufferDays: number }}
 */
function getEtaPayloadFromElement(element) {
  const embedded = element.querySelector('script[type="application/json"][data-eta-windows]');

  if (embedded?.textContent) {
    try {
      const parsed = JSON.parse(embedded.textContent);
      if (parsed && typeof parsed === 'object') {
        return {
          etaWindows: /** @type {import('@theme/delivery-estimate').EtaWindowsMap} */ (
            parsed.etaWindows ?? {}
          ),
          bufferDays: Number(parsed.bufferDays) || 0,
        };
      }
    } catch {
      // Fall through.
    }
  }

  return { etaWindows: {}, bufferDays: 0 };
}

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} triggerState
 * @property {HTMLElement} triggerEstimate
 * @property {HTMLElement} panel
 * @property {HTMLUListElement} list
 * @property {HTMLElement} deliveryDate
 * @property {HTMLElement} deliveryDateValue
 *
 * @extends {Component<Refs>}
 */
class ProductStockStatusComponent extends Component {
  requiredRefs = ['trigger', 'triggerState', 'triggerEstimate', 'panel', 'list', 'deliveryDate', 'deliveryDateValue'];

  /** @type {boolean} */
  #expanded = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.trigger.addEventListener('click', this.#toggle);
    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener(STATE_CHANGE_EVENT, this.#handleExternalStateChange);
    window.addEventListener('storage', this.#handleStorageChange);

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.trigger.removeEventListener('click', this.#toggle);
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener(STATE_CHANGE_EVENT, this.#handleExternalStateChange);
    window.removeEventListener('storage', this.#handleStorageChange);

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * @param {HTMLElement} source
   */
  #applyDataFromElement(source) {
    const attributes = [
      'data-inventory-quantity',
      'data-state-classifications',
      'data-available-states',
      'data-stock-by-location',
    ];

    for (const attribute of attributes) {
      const value = source.getAttribute(attribute);

      if (value !== null) {
        this.setAttribute(attribute, value);
      }
    }

    const newWindowsScript = source.querySelector('script[type="application/json"][data-eta-windows]');
    const currentWindowsScript = this.querySelector('script[type="application/json"][data-eta-windows]');

    if (newWindowsScript && currentWindowsScript) {
      currentWindowsScript.textContent = newWindowsScript.textContent;
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const { newProduct, html } = event.detail.data;

    if (newProduct) {
      this.dataset.productId = newProduct.id;

      const newComponent = html.querySelector(`product-stock-status[data-product-id="${newProduct.id}"]`);

      if (newComponent instanceof HTMLElement) {
        this.#applyDataFromElement(newComponent);
      }

      this.updatedCallback();
      this.#collapse();
      this.#render();
      return;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newComponent = html.querySelector(
      `product-stock-status[data-product-id="${this.dataset.productId}"]`
    );

    if (!newComponent) return;

    if (newComponent instanceof HTMLElement) {
      this.#applyDataFromElement(newComponent);
    }

    morph(this, newComponent, { childrenOnly: true });
    this.updatedCallback();
    this.#render();
  };

  #handleExternalStateChange = () => {
    this.#collapse();
    this.#render();
  };

  /**
   * @param {StorageEvent} event
   */
  #handleStorageChange = (event) => {
    if (event.key !== STORAGE_KEY) return;

    this.#render();
  };

  #handleDocumentClick = (event) => {
    if (!this.#expanded) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#collapse();
  };

  #toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#expanded) {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  #expand() {
    this.#expanded = true;
    this.dataset.expanded = 'true';
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.refs.panel.setAttribute('aria-hidden', 'false');
    this.refs.panel.removeAttribute('inert');
  }

  #collapse() {
    this.#expanded = false;
    this.dataset.expanded = 'false';
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('aria-hidden', 'true');
    this.refs.panel.setAttribute('inert', '');
  }

  /**
   * @returns {string[]}
   */
  #getStateCodes() {
    const raw = this.dataset.stateCodes;

    if (!raw) return [];

    return raw.split(',').map((code) => code.trim()).filter(Boolean);
  }

  /**
   * @returns {Record<string, string>}
   */
  #getStateNames() {
    return /** @type {Record<string, string>} */ (parseJsonAttribute('data-state-names', this) ?? {});
  }

  /**
   * @returns {Record<string, string>}
   */
  #getClassifications() {
    return /** @type {Record<string, string>} */ (parseJsonAttribute('data-state-classifications', this) ?? {});
  }

  /**
   * @returns {string[]}
   */
  #getAvailableStates() {
    return /** @type {string[]} */ (parseJsonAttribute('data-available-states', this) ?? []);
  }

  /**
   * Per-state warehouse stock from variant metafield shipping.stock_by_location.
   * @returns {Record<string, boolean>}
   */
  #getStockByLocation() {
    return /** @type {Record<string, boolean>} */ (parseJsonAttribute('data-stock-by-location', this) ?? {});
  }

  /**
   * @returns {string}
   */
  #getUnavailableLabel() {
    return this.dataset.unavailableLabel || 'Unavailable';
  }

  /**
   * @returns {{ etaWindows: import('@theme/delivery-estimate').EtaWindowsMap, bufferDays: number }}
   */
  #getEtaPayload() {
    return getEtaPayloadFromElement(this);
  }

  /**
   * @param {string} stateCode
   * @returns {string | null}
   */
  #getDisplayCode(stateCode) {
    return this.#getClassifications()[stateCode] ?? null;
  }

  /**
   * @param {string} stateCode
   * @returns {ReturnType<typeof calculateDeliveryEstimate>}
   */
  #getEstimateForState(stateCode) {
    // State warehouse explicitly out of stock: treat as zero so R resolves to R - No Stock.
    // Missing field or state key falls back to national inventory (existing behaviour).
    const stockState = STOCK_STATE_FALLBACK[stateCode] ?? stateCode;
    const inventoryQuantity =
      this.#getStockByLocation()[stockState] === false ? 0 : Number(this.dataset.inventoryQuantity);
    const displayCode = this.#getDisplayCode(stateCode);

    if (!this.#isStateListedForProduct(stateCode, displayCode)) {
      return null;
    }

    const { etaWindows, bufferDays } = this.#getEtaPayload();

    return calculateDeliveryEstimate({
      etaWindows,
      bufferDays,
      stateCode,
      displayCode,
      inventoryQuantity: Number.isFinite(inventoryQuantity) ? inventoryQuantity : null,
      sampleLabel: this.dataset.sampleLabel || undefined,
    });
  }

  /**
   * @param {string} stateCode
   * @param {string | null} displayCode
   * @returns {boolean}
   */
  #isStateListedForProduct(stateCode, displayCode) {
    const classifications = this.#getClassifications();
    const availableStates = this.#getAvailableStates();
    const hasClassifications = Object.keys(classifications).length > 0;
    const hasAvailableStates = availableStates.length > 0;

    if (!hasClassifications && !hasAvailableStates) {
      return false;
    }

    if (hasAvailableStates && !availableStates.includes(stateCode)) {
      return false;
    }

    if (hasClassifications && !displayCode) {
      return false;
    }

    return true;
  }

  /**
   * @param {string} stateCode
   * @returns {string}
   */
  #getStatusLabelForState(stateCode) {
    const estimate = this.#getEstimateForState(stateCode);

    return estimate?.rangeLabel ?? this.#getUnavailableLabel();
  }

  #render() {
    const stateCodes = this.#getStateCodes();
    const stateNames = this.#getStateNames();
    const selectedState = sessionStorage.getItem(STORAGE_KEY);
    const placeholder = this.dataset.placeholder || 'Choose State';

    this.refs.list.replaceChildren();

    for (const code of stateCodes) {
      const statusLabel = this.#getStatusLabelForState(code);
      const name = stateNames[code] ?? code;

      const item = document.createElement('li');
      item.className = 'product-stock-status__option';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-stock-status__option-button';
      button.dataset.stateCode = code;

      if (code === selectedState) {
        button.setAttribute('aria-current', 'true');
      }

      const nameEl = document.createElement('span');
      nameEl.className = 'product-stock-status__option-state';
      nameEl.textContent = name;

      const estimateEl = document.createElement('span');
      estimateEl.className = 'product-stock-status__option-estimate';
      estimateEl.textContent = statusLabel;

      button.append(nameEl, estimateEl);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.#selectState(code, name);
      });
      item.append(button);
      this.refs.list.append(item);
    }

    this.#renderSelection(selectedState, placeholder, stateNames);
  }

  /**
   * @param {string | null} selectedState
   * @param {string} placeholder
   * @param {Record<string, string>} stateNames
   */
  #renderSelection(selectedState, placeholder, stateNames) {
    if (!selectedState) {
      this.refs.triggerState.textContent = placeholder;
      this.refs.triggerEstimate.textContent = '';
      this.refs.deliveryDate.hidden = true;
      return;
    }

    const name = stateNames[selectedState] ?? selectedState;
    const estimate = this.#getEstimateForState(selectedState);
    const statusLabel = this.#getStatusLabelForState(selectedState);

    this.refs.triggerState.textContent = name;
    this.refs.triggerEstimate.textContent = statusLabel;

    if (estimate?.showDeliveryDate && estimate.deliveryDate) {
      this.refs.deliveryDateValue.textContent = estimate.deliveryDate;
      this.refs.deliveryDate.hidden = false;
    } else {
      this.refs.deliveryDate.hidden = true;
    }
  }

  /**
   * @param {string} code
   * @param {string} name
   */
  #selectState(code, name) {
    publishStateChange(code, name);
    this.#collapse();
    this.#render();
  }
}

if (!customElements.get('product-stock-status')) {
  customElements.define('product-stock-status', ProductStockStatusComponent);
}
